import React, { useEffect, useRef, useState } from "react";
import {
  MessageCircle, ArrowRightCircle, UserCheck, RotateCcw, FileText,
  Send, ScrollText, UserPlus,
} from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { useLanguage } from "../../context/LanguageContext";
import { api } from "../../lib/api";
import { toast } from "sonner";

const formatAbsolute = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const formatRelative = (iso) => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "just now";
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return "";
  }
};

const eventIcon = {
  comment: MessageCircle,
  status_change: ArrowRightCircle,
  claim: UserCheck,
  release: RotateCcw,
  reassign: UserPlus,
  file_change: FileText,
};

// Role chip palette per Phase-5 spec: agent=slate, reviewer=blue, admin=amber
const roleChip = {
  admin: "bg-amber-50 text-amber-800 border-amber-200",
  agent: "bg-slate-100 text-slate-700 border-slate-200",
  reviewer: "bg-blue-50 text-blue-700 border-blue-200",
};

const iconBubble = {
  admin: "bg-amber-50 text-amber-700",
  agent: "bg-slate-100 text-slate-700",
  reviewer: "bg-blue-50 text-blue-700",
};

export const StatusThread = ({ caseId, caseStatus, onActivity }) => {
  const { t } = useLanguage();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const lastFetchRef = useRef(null);

  const fetchEvents = async () => {
    if (!caseId) return;
    try {
      const { data } = await api.get(`/cases/${caseId}/events`);
      setEvents(data);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, caseStatus]);

  const onSend = async () => {
    if (!draft.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/cases/${caseId}/comments`, { message: draft.trim() });
      toast.success(t("toast_comment_sent"));
      setDraft("");
      await fetchEvents();
      if (onActivity) onActivity();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err_generic"));
    } finally {
      setSending(false);
    }
  };

  const isLocked = caseStatus === "closed";

  return (
    <section
      className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 sm:p-8 space-y-6"
      data-testid="status-thread"
    >
      <div className="flex items-center gap-2">
        <ScrollText className="h-5 w-5 text-[#0A2540]" />
        <h3
          className="font-display text-xl tracking-tight font-medium text-[#0A2540]"
          data-testid="audit-log-title"
        >
          {t("audit_log_title")}
        </h3>
      </div>

      <div className="space-y-1" data-testid="audit-log-list">
        {loading ? (
          <div className="flex justify-center py-6">
            <div className="h-5 w-5 border-2 border-slate-300 border-t-[#0A2540] rounded-full animate-spin" />
          </div>
        ) : events.length === 0 ? (
          <p className="text-sm text-slate-400 italic" data-testid="status-thread-empty">
            {t("status_thread_empty")}
          </p>
        ) : (
          events.map((e, i) => {
            const Icon = eventIcon[e.type] || MessageCircle;
            const role = e.actor_role || "agent";
            const isLast = i === events.length - 1;
            return (
              <div
                key={e.id}
                className="flex gap-3"
                data-testid={`event-${e.type}`}
              >
                <div className="flex flex-col items-center">
                  <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${iconBubble[role] || iconBubble.agent}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  {!isLast ? (
                    <div className="flex-1 w-px bg-slate-200 my-1" />
                  ) : null}
                </div>
                <div className="flex-1 pb-5 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="text-sm font-medium text-[#0A2540]">{e.actor_name}</span>
                    <span
                      className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${roleChip[role] || roleChip.agent}`}
                      data-testid={`event-role-chip-${role}`}
                    >
                      {t(`role_${role}`)}
                    </span>
                    <span
                      className="text-xs text-slate-400"
                      title={formatAbsolute(e.created_at)}
                      data-testid={`event-time-${e.id}`}
                    >
                      {formatRelative(e.created_at)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {t(`event_type_${e.type}`)}
                    {e.from_status && e.to_status && e.from_status !== e.to_status ? (
                      <>
                        : <span className="font-medium">{t(`case_status_${e.from_status}`)}</span> →{" "}
                        <span className="font-medium">{t(`case_status_${e.to_status}`)}</span>
                      </>
                    ) : null}
                  </p>
                  {e.message ? (
                    <div className="mt-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm text-[#0A2540] whitespace-pre-wrap break-words">
                      {e.message}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="border-t border-slate-200 pt-5 space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          {t("status_thread_title")}
        </p>
        {isLocked ? (
          <p className="text-sm text-slate-500 italic" data-testid="status-thread-locked">
            {t("status_thread_locked")}
          </p>
        ) : (
          <div className="flex gap-2 items-end">
            <Textarea
              rows={2}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={t("status_thread_comment_placeholder")}
              className="flex-1"
              data-testid="comment-input"
            />
            <Button
              onClick={onSend}
              disabled={!draft.trim() || sending}
              className="bg-[#0A2540] hover:bg-[#051626] text-white"
              data-testid="comment-send-button"
            >
              <Send className="h-4 w-4 mr-2" />
              {t("status_thread_send")}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default StatusThread;
